import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@roger/ui-components';
import { formatDateTimeInAgencyZone } from '../../utils/dateUtils';
import { useCanRunShpCheck, useShpCheck } from '../../hooks/useShpCheck';
import { getOasisShpLastCheckVisitsOasisManagerVisitIdShpCheckGet } from '../../services/api/sdk.gen';
import type {
  OasisExportReadiness,
  ShpLastCheckResponse,
} from '../../services/api/types.gen';

interface ShpCheckControlProps {
  visitId: string;
  /** The row's latest check. Left out, the control loads it itself. */
  lastCheck?: ShpLastCheckResponse | null;
  /** Known by the caller (a row): the chart is one the nightly run would never send. */
  unsendable?: boolean;
  onCmsErrors: (readiness: OasisExportReadiness) => void;
}

/** "Last SHP check 10/27/2026, 4:05 PM by Ann Lee", and whether it is stale. */
export const ShpLastCheckLine: React.FC<{
  lastCheck: ShpLastCheckResponse;
}> = ({ lastCheck }) => (
  <p className='text-xs text-muted-foreground'>
    Last SHP check {formatDateTimeInAgencyZone(lastCheck.checked_at)}
    {lastCheck.checked_by_name ? ` by ${lastCheck.checked_by_name}` : ''}
    {lastCheck.changed_since && (
      <span className='font-medium text-foreground'> · Changed since</span>
    )}
  </p>
);

/**
 * SHP Check: sends the chart to SHP and opens SHP's alerts in a new window.
 * The caller shows it only where useCanRunShpCheck allows.
 */
export const ShpCheckControl: React.FC<ShpCheckControlProps> = ({
  visitId,
  lastCheck,
  unsendable: unsendableKnown,
  onCmsErrors,
}) => {
  const loads = lastCheck === undefined;
  const [shown, setShown] = useState<ShpLastCheckResponse | null>(
    lastCheck ?? null
  );
  // Loaded with the last check: why the nightly run would never send it.
  const [loadedUnsendable, setUnsendable] = useState(false);
  const unsendable = unsendableKnown ?? loadedUnsendable;
  // Not offered until we know it can run; a failed load leaves the server to refuse.
  const [settled, setSettled] = useState(!loads);

  useEffect(() => {
    if (!loads) setShown(lastCheck ?? null);
  }, [lastCheck, loads]);

  useEffect(() => {
    if (!loads) return;
    let cancelled = false;
    void getOasisShpLastCheckVisitsOasisManagerVisitIdShpCheckGet({
      path: { visit_id: visitId },
    })
      .then(response => {
        if (cancelled || response.error) return;
        setShown(response.data?.last_check ?? null);
        setUnsendable(Boolean(response.data?.unsendable_reason));
      })
      .catch(error => {
        console.error('Could not load the last SHP check', error);
      })
      .finally(() => {
        if (!cancelled) setSettled(true);
      });
    return () => {
      cancelled = true;
    };
  }, [loads, visitId]);

  const { run, checkingId } = useShpCheck({
    onChecked: useCallback((_id: string, last: ShpLastCheckResponse | null) => {
      if (last) setShown(last);
    }, []),
    onCmsErrors: useCallback(
      (_id: string, readiness: OasisExportReadiness) => onCmsErrors(readiness),
      [onCmsErrors]
    ),
  });
  const checking = checkingId === visitId;

  if (!settled) return null;
  // The button is for a chart the nightly run would send; a check made
  // before it became unsendable still shows.
  if (unsendable) return shown ? <ShpLastCheckLine lastCheck={shown} /> : null;
  return (
    <div className='flex flex-col items-start gap-1'>
      <Button
        type='button'
        variant='outline'
        size='sm'
        disabled={checking}
        onClick={() => void run(visitId)}
      >
        {checking ? 'Checking…' : 'SHP Check'}
      </Button>
      {shown && <ShpLastCheckLine lastCheck={shown} />}
    </div>
  );
};

interface ChartShpCheckProps {
  visitId: string;
  isCmsSubmittable: boolean;
  isDeleted: boolean;
  isMissed: boolean;
  onCmsErrors: (readiness: OasisExportReadiness) => void;
}

/** SHP Check on a chart: a CMS assessment, for a user allowed to run it. */
export const ChartShpCheck: React.FC<ChartShpCheckProps> = ({
  visitId,
  isCmsSubmittable,
  isDeleted,
  isMissed,
  onCmsErrors,
}) => {
  const canRun = useCanRunShpCheck();
  if (!canRun || !isCmsSubmittable || isDeleted || isMissed) return null;
  return <ShpCheckControl visitId={visitId} onCmsErrors={onCmsErrors} />;
};
